const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const MIN_DELIVERY_LEAD_DAYS = 3;
export const DEFAULT_DELIVERY_WINDOW_SPAN_DAYS = 4;
export const MAX_DELIVERY_HORIZON_DAYS = 180;

export const CALENDAR_DATE_FORMAT = {
  pattern: CALENDAR_DATE_PATTERN,

  validate(value: string | undefined | null): boolean {
    return this.toTimestamp(value) !== undefined;
  },

  toTimestamp(value: string | undefined | null): number | undefined {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!CALENDAR_DATE_PATTERN.test(trimmed)) return undefined;
    const parsed = Date.parse(`${trimmed}T12:00:00Z`);
    if (Number.isNaN(parsed)) return undefined;
    return new Date(parsed).toISOString().startsWith(trimmed)
      ? parsed
      : undefined;
  },
} as const;

export function calendarDateToTimestamp(value: string): number {
  const parsed = CALENDAR_DATE_FORMAT.toTimestamp(value);
  if (parsed !== undefined) return parsed;
  return CALENDAR_DATE_PATTERN.test(value.trim())
    ? Number.NaN
    : new Date(value).getTime();
}

export function currentUtcCalendarDate(now: number = Date.now()): string {
  return new Date(now).toISOString().slice(0, 10);
}

export function addCalendarDays(value: string, days: number): string {
  const timestamp = CALENDAR_DATE_FORMAT.toTimestamp(value);
  if (timestamp === undefined) return value;
  return new Date(timestamp + days * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

export function getEarliestDeliveryDate(
  leadTimeDays: number,
  now: number = Date.now(),
): string {
  const normalizedLeadTime = Number.isFinite(leadTimeDays)
    ? Math.max(MIN_DELIVERY_LEAD_DAYS, Math.ceil(leadTimeDays))
    : MIN_DELIVERY_LEAD_DAYS;
  return addCalendarDays(currentUtcCalendarDate(now), normalizedLeadTime);
}

export function latestDeliveryDateFromEarliest(
  earliestDate: string,
  now: number = Date.now(),
): string {
  const horizon = addCalendarDays(currentUtcCalendarDate(now), MAX_DELIVERY_HORIZON_DAYS);
  const defaultWindowEnd = addCalendarDays(earliestDate, DEFAULT_DELIVERY_WINDOW_SPAN_DAYS);
  return defaultWindowEnd > horizon ? defaultWindowEnd : horizon;
}

export function getLatestDeliveryDate(
  leadTimeDays: number,
  now: number = Date.now(),
): string {
  return latestDeliveryDateFromEarliest(getEarliestDeliveryDate(leadTimeDays, now), now);
}

export function utcCalendarDate(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

export function calendarDayNoonUtc(timestamp: number): number {
  return Date.parse(`${utcCalendarDate(timestamp)}T12:00:00Z`);
}

export function isSameUtcCalendarDay(left: number, right: number): boolean {
  return utcCalendarDate(left) === utcCalendarDate(right);
}

const HALF_DAY_MS = 12 * 60 * 60 * 1000;

export function calendarDayEndUtc(timestamp: number): number {
  return calendarDayNoonUtc(timestamp) + HALF_DAY_MS - 1;
}

export function calendarDayEndOrUndefined(
  timestamp: number | null | undefined,
): number | undefined {
  return timestamp == null ? undefined : calendarDayEndUtc(timestamp);
}

export function calendarDateToEndOfDayTimestamp(value: string): number {
  const noon = calendarDateToTimestamp(value);
  return Number.isNaN(noon) ? noon : noon + HALF_DAY_MS - 1;
}

export function formatCalendarDate(value: string): string {
  const timestamp = CALENDAR_DATE_FORMAT.toTimestamp(value);
  if (timestamp === undefined) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(timestamp);
}

const CALENDAR_MONTH_DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export function formatCalendarMonthDay(timestamp: number): string {
  return CALENDAR_MONTH_DAY.format(timestamp);
}
